<template>
  <div
    v-if="pitcherSettings?.use_collection_player_with_annotations && isClient"
    ref="containerRef"
    class="w-full h-full max-w-[100%] max-h-[100%] flex justify-center items-center relative overflow-auto"
    @click="handleContainerInteraction"
    @pointerdown="handleContainerInteraction"
    @touchstart="handleContainerInteraction"
  >
    <ImageWithAnnotations
      v-if="image && imageDimensions"
      :key="imageWithAnnotationKey"
      :aspect-ratio="imageDimensions.aspectRatio"
      :container-height="containerHeight"
      :container-width="containerWidth"
      :image-height="imageDimensions.height"
      :image-width="imageDimensions.width"
      :is-active="isActive"
      :scaling-mode="scalingMode"
      :target-image="image"
      :zoom="zoom"
      @repaint="imageWithAnnotationKey = Symbol()"
    />
  </div>

  <img
    v-else
    :alt="item.file.name"
    class="w-full max-w-full"
    :class="fitToContainerWidth ? 'block ma-auto max-h-none' : 'h-full'"
    :src="image"
    :style="{ objectFit: fitToContainerWidth ? 'unset' : 'contain' }"
  />
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, inject, type ComputedRef, computed } from 'vue'
import { useLoadingBar } from 'naive-ui'
import { useWindowSize, useElementSize } from '@vueuse/core'
import { debounce } from 'lodash-es'
import useImageCache from '../composables/image-cache.use'
import type { CollectionPage, CollectionSlide } from '@collection-player/types/collection-player.type'
import { useAppStore } from '@collection-player/stores/app.store'
import ImageWithAnnotations from './ImageWithAnnotations.vue'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types.ts'
import type { InstanceSettings } from '@lib/types/instanceSettings.types.ts'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const props = defineProps<{
  fitToContainerWidth: boolean
  isActive: boolean
  zoom: number
  item: CollectionSlide
}>()

const WAIT_BEFORE_HIGH_RESOLUTION_FETCH_IN_MS = 250
const DEBOUNCE_FETCH_IN_MS = 250

const { api } = useAppStore()
const imageCache = useImageCache()
const { width: windowWidth } = useWindowSize()
const loadingBar = useLoadingBar()
const image = ref()
const isLoading = ref(false)
const isLoaded = ref(false)
const imageDimensions = ref<{ width: number; height: number; aspectRatio: number } | null>(null)
let timeoutId: NodeJS.Timeout | null = null
const containerRef = ref<HTMLDivElement | null>(null)
const { width: containerWidth, height: containerHeight } = useElementSize(containerRef)
const getRequiredImageWidth = () => Math.ceil(windowWidth.value * window.devicePixelRatio * props.zoom)
const imageWithAnnotationKey = ref(Symbol())

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const isClient = typeof window !== 'undefined'

const getCacheId = (documentId: string, pageIndex?: number) =>
  pageIndex !== undefined ? `${documentId}-${pageIndex}` : documentId

const fetchPageImage = async (item: CollectionPage) => {
  const fileId = item.file.id
  const documentId = item.file.pspdfkit_document_id
  const pageIndex = item.slide.index

  if (!fileId && !documentId) {
    return
  }

  const width = getRequiredImageWidth()

  const arrayBuffer = await api.renderPageAsImage({
    fileId,
    documentId: documentId ?? undefined,
    pageIndex,
    params: {
      width: getRequiredImageWidth(),
    },
  })

  if (!arrayBuffer) {
    return
  }

  const blob = new Blob([arrayBuffer])
  const url = URL.createObjectURL(blob)

  imageCache.set(getCacheId(documentId ?? fileId, pageIndex), {
    width,
    url,
  })

  return url
}

const { annotationLayer } = useAppStore()
const { activeTool, isVisibleMenu, scalingMode } = annotationLayer

const handleContainerInteraction = () => {
  if (activeTool.value || !isVisibleMenu.value) {
    return
  }

  annotationLayer.showMenu(false)
}

const getCachedContent = (item: CollectionSlide) => {
  const documentId = item.file.pspdfkit_document_id
  const fileId = item.file.id
  const id = documentId ?? fileId
  const pageIndex = 'slide' in item ? item.slide.index : undefined

  return id ? imageCache.get(getCacheId(id, pageIndex)) : undefined
}

const isPageCachedWithEnoughSize = (item: CollectionSlide) => {
  const cachedValue = getCachedContent(item)
  return cachedValue && cachedValue.width >= getRequiredImageWidth()
}

const fetchDimensions = (url: string): Promise<{ width: number; height: number; aspectRatio: number } | null> => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null)
      return
    }

    const img = new Image()
    img.src = url

    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
          aspectRatio: img.naturalWidth / img.naturalHeight,
        })
      } else {
        resolve(null)
      }
    }

    img.onerror = () => {
      resolve(null)
    }
  })
}

const setInitialImage = async (item: CollectionSlide) => {
  const cachedContent = getCachedContent(item)
  const skipPspdfkitWait = launchDarkly.value?.skip_pspdfkit_wait_for_high_res_thumbs

  if (cachedContent && isPageCachedWithEnoughSize(item)) {
    image.value = cachedContent.url
    imageDimensions.value = await fetchDimensions(image.value)
    isLoaded.value = true
  } else if (item.type === 'file') {
    image.value = item.file.content_url
    imageDimensions.value = await fetchDimensions(image.value)
    isLoaded.value = true
  } else {
    image.value = item.slide.url
    imageDimensions.value = await fetchDimensions(image.value)

    // If flag is enabled and thumbnails are high enough resolution, mark as loaded
    if (skipPspdfkitWait) {
      isLoaded.value = true
    } else {
      createWatcher(item)
    }
  }
}

const fetchContentImage = async (item: CollectionPage) => {
  if (!props.isActive) {
    return
  }

  isLoading.value = true
  loadingBar.start()

  try {
    image.value = await fetchPageImage(item)
    loadingBar.finish()
    isLoaded.value = true
  } catch (error) {
    console.error('Failed to fetch slide image', error)
    loadingBar.error()
  } finally {
    isLoading.value = false
  }
}

const debouncedFetchContent = debounce(fetchContentImage, DEBOUNCE_FETCH_IN_MS)

const createWatcher = (item: CollectionPage) =>
  watch(
    () => props.isActive,
    (isActive) => {
      if (!isActive || isLoading.value || isLoaded.value) return

      isLoading.value = true

      timeoutId = setTimeout(() => {
        if (props.isActive) {
          fetchContentImage(item)
        } else {
          isLoading.value = false
        }
      }, WAIT_BEFORE_HIGH_RESOLUTION_FETCH_IN_MS)
    },
    { immediate: true },
  )

setInitialImage(props.item)

watch([windowWidth, () => props.zoom, () => props.isActive], () => {
  const skipPspdfkitWait = launchDarkly.value?.skip_pspdfkit_wait_for_high_res_thumbs

  if (
    !props.isActive ||
    !isLoaded.value ||
    isLoading.value ||
    props.item.type === 'file' ||
    isPageCachedWithEnoughSize(props.item) ||
    skipPspdfkitWait
  ) {
    return
  }

  debouncedFetchContent(props.item)
})

onBeforeUnmount(() => {
  if (timeoutId !== null) {
    clearTimeout(timeoutId)
  }
})
</script>
